import { ModuleAccess } from '../../../types/auth.model';
import { Link, useSearchParams } from 'react-router-dom';
import React, { useState } from 'react';
import { useFetchData } from '../../../hooks';
import { PageableResponse } from '../../../types';
import {
  convertDate,
  dateFormat,
  dateTimeFormat,
  getEnumValue,
} from '../../../lib/utils';
import { PurchaseRequest } from '../../../types/PurchaseRequest';
import StatusTag from '../../../components/ui/StatusTag';
import { RequestStatusEnum, Resource } from '../../../lib/enums';
import { Table } from 'antd';
import InfoModal from '../../../components/ui/modal/InfoModal';
import dayjs from 'dayjs';

const PurchaseRequestTable = ({
  api,
  moduleName,
}: {
  api: string;
  moduleName: string;
  userModuleAccess: ModuleAccess | undefined;
  userRole?: string;
}) => {
  const [searchParams] = useSearchParams();
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);

  const startDate =
    searchParams.get('start') === undefined ||
    searchParams.get('start') === null
      ? dayjs().subtract(6, 'month').format(dateFormat)
      : searchParams.get('start');

  const endDate =
    searchParams.get('end') === undefined || searchParams.get('end') === null
      ? dayjs().add(2, 'month').format(dateFormat)
      : searchParams.get('end');

  const { data, isLoading } = useFetchData<PageableResponse<PurchaseRequest>>(
    api,
    {
      resource: searchParams.get('resource') || Resource.FULL,
      search: searchParams.get('search') || '',
      from: startDate,
      to: endDate,
      status: getEnumValue(RequestStatusEnum, searchParams.get('status') ?? ''),
      length: parseInt(searchParams.get('length') || '999999'),
    },
  );

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      render: (_text: string, record: PurchaseRequest) => {
        return (
          <Link to={`/purchase-request/${record.id}?action=view`}>
            <span className={'text-blue-400 font-semibold'}>
              {record.code || record.id}
            </span>
          </Link>
        );
      },
    },
    { title: 'Required Date', dataIndex: 'requiredDate' },
    { title: 'Source', dataIndex: 'source' },
    {
      title: 'Destination',
      dataIndex: 'destination',
      render: (_text: string, record: PurchaseRequest) =>
        `${record?.destination?.description === undefined ? '' : record.destination?.description}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      onFilter: (value: any, record: PurchaseRequest) => record.status === value,
      render: (_text: any, record: PurchaseRequest) => (
        <StatusTag status={record.status} />
      ),
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      sorter: (a: PurchaseRequest, b: PurchaseRequest) => {
        const createdByComparison = a.createdBy.localeCompare(b.createdBy);
        return createdByComparison !== 0
          ? createdByComparison
          : (new Date(a.timeCreated || 0).getTime() - new Date(b.timeCreated || 0).getTime());
      },
      render: (_text: string, record: PurchaseRequest) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.createdBy}</span>
            {record.timeCreated != null && (
              <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Update By/Date',
      dataIndex: 'updatedBy',
      render: (_text: string, record: PurchaseRequest) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.updatedBy}</span>
            {record.timeUpdated != null && (
              <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
      sorter: (a: PurchaseRequest, b: PurchaseRequest) => {
        const updatedByComparison = a.updatedBy.localeCompare(b.updatedBy);
        return updatedByComparison !== 0
          ? updatedByComparison
          : (new Date(a.timeUpdated || 0).getTime() - new Date(b.timeUpdated || 0).getTime());
      },
    },
  ];

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  return (
    <>
      <div className={'flex flex-col h-[calc(79vh)] overflow-auto'}>
        <Table
          rowKey={(record) => record.id}
          dataSource={data?.data}
          loading={isLoading}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          columns={columns}
        />
      </div>
      <InfoModal
        visible={isInfoModalOpen}
        message={`${moduleName} successfully deleted`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default PurchaseRequestTable;
